// @ts-nocheck

import {Citation as originalCitation} from "../../../main/index.js";
import cloneDeep from "lodash/cloneDeep.js";

// Testing magic.
// See: https://railsware.com/blog/mocking-es6-module-import-without-dependency-injection/
let Citation = originalCitation;

// TODO: hide bulk update button (in v1 editor) on pages with mixed citation versions

/**
 * Utility functions for dealing with citations
 */

/**
 * Returns bulk-approved citation data for this page,
 * if it exists. Returns null if no bulk-approved data exists.
 * This is only called if we've determined that the page should
 * use a structured (non-law) citation.
 *
 * @param {Object}   outline                       see self.store.outline in main.js
 * @param {function} getAutoGeneratedCitationData  A function that can be called to collect fresh citation data
 * @param {function} updateOldAnnotation           A function that can be called to update v1 citations
 * @return object|null
 */
export async function getBulkCitationData(outline, getAutoGeneratedCitationData, updateOldAnnotation) {
	const annotations = thisPageAnnotationsIterator(outline);
	let cit = Citation.getBulkCitationData(annotations);
	if (cit === null) {
		cit = await getAutoGeneratedCitationData();

		if (typeof cit === "object") {
			for (const ann of thisPageAnnotationsIterator(outline)) {
				if (!("citation" in ann))
					continue;

				if (Citation.getCitationVersion(ann.citation) >= 2) {
					// Always include existing source notes, even if we're not copying citation data in general.
					// This is important for law/unstructured citations.

					if (ann.citation.source_notes) {
						cit.source_notes = cloneDeep(ann.citation.source_notes);
						break;
					}
				}
			}
		}
	}

	// Now update any v1 citations, if appropriate
	if (cit !== null && Citation.getCitationVersion(cit) >= 2) {
		const annotations = thisPageAnnotationsIterator(outline); // refresh iterator
		const citationsToUpdate = Citation.getCitationsToUpdate(annotations, cit);
		for (const c of citationsToUpdate)
			updateOldAnnotation(c.annotation_uuid, c.citation); // async, but don't wait for it
	}

	return cit;
}

/**
 * Determine any citations on the page that may need to be updated.
 *
 * @param {string} originatingAnnotationUuid
 * @param {Object} citation
 * @param {Object} outline
 * @return {Object[]}
 */
export function calcBulkUpdates(originatingAnnotationUuid, citation, outline) {
	try { // be really careful
		const annotations = thisPageAnnotationsIterator(outline);
		return Citation.calcBulkUpdates(originatingAnnotationUuid, citation, annotations);
	} catch (e) {
		console.error(e);
		return [];
	}
}

/**
 * Determine any citations on pages that match the given pageData.
 *
 * @param {string} originatingAnnotationUuid
 * @param {Object} citation
 * @param {Object} outline
 * @param {Object} pageData
 * @return {Object[]}
 */
export function calcBulkUpdatesWithPageData(originatingAnnotationUuid, citation, outline, pageData) {
	try { // be really careful
		const annotations = givenPageAnnotationsIterator(outline, pageData);
		return Citation.calcBulkUpdates(originatingAnnotationUuid, citation, annotations);
	} catch (e) {
		console.error(e);
		return [];
	}
}

/**
 * Updates citation on all annotations where it's missing.
 *
 * @param {Object}   outline                       see self.store.outline in main.js
 * @param {function} getAutoGeneratedCitationData  A function that can be called to collect fresh citation data
 * @param {function} update                        A function that can be called to update annotations
 * @return {Promise<{annotations: number, citations: number, errors: number}>}
 */
export async function updateAllMissingCitations(outline, getAutoGeneratedCitationData, update) {
	// Keyed by URL, value is a single annotation
	const todo = {};
	const anns = annotationsIterator(outline);

	for (const ann of anns) {
		if (!shouldCitationBeUpdated(ann))
			continue;

		const url = ann.pageData.cleanUrl ? ann.pageData.cleanUrl : ann.pageData.pageUrl;
		todo[url] = ann;
	}

	let annotations = 0, // how many annotations were updated
		citations = 0, // how many citations were generated
		errors = 0;

	for (const ann of Object.values(todo)) {
		try {
			const cit = await getAutoGeneratedCitationData(ann.pageData);
			const count = await update(ann.uuid, cit, undefined, ann.pageData);
			if (!cit.auto_citation_error) {
				annotations += count;
				citations++;
			} else
				errors++;
		} catch (e) {
			console.error("PN: Error updating citation", {
				annotation_uuid: ann.uuid,
				pageData:        ann.pageData,
				error:           e
			});
			errors++;
		}
	}

	const report = {annotations, citations, errors};

	console.log("PN: Updated missing citations", report);
	return report;
}

/**
 * Returns true if updateAllMissingCitations() has something to do.
 *
 * @param {Object} outline
 * @return {boolean}
 */
export function missingCitationsExist(outline) {
	for (const ann of annotationsIterator(outline)) {
		if (shouldCitationBeUpdated(ann))
			return true;
	}
	return false;
}

/**
 * Returns true if all the following conditions are true:
 *
 * - Annotation's URL is public (no local files)
 * - URL is not blacklisted
 * - Citation version is > 1
 * - Citation is determined to be "structured"
 * - Citation is empty (only considers the `rendered_citation` field)
 *
 * @param {Object} ann
 * @return {boolean}
 */
function shouldCitationBeUpdated(ann) {
	const {citation, pageData} = ann;

	if (!pageData || originalCitation.getCitationVersion(citation) < 2 || !citation.structured)
		return false;

	const url = pageData.cleanUrl ? pageData.cleanUrl : pageData.pageUrl;
	if (url.substring(0, 5).toLowerCase() === "file:")
		return false;
	if (originalCitation.isAutoCitationBlacklisted(url))
		return false;

	// Is it empty?
	const div = document.createElement("div");
	div.innerHTML = citation.rendered_citation;
	const text = ((div.textContent ?? div.innerText) || "").trim();
	return text === "" || text === ".";
}

/**
 * Just a helper function. Exported below mostly for testing purposes.
 *
 * For those not quite paying attention, this is using a generator function.
 *
 * @param {object} outline
 * @return function
 */
export function thisPageAnnotationsIterator(outline) {
	return annotationsIterator(outline, annotation => annotation.currentPage);
}

function givenPageAnnotationsIterator(outline, pageData) {
	const condition = annotation => {
		if (!annotation.pageData)
			return false;

		return annotation.pageData.pageUrl === pageData.pageUrl;
	};

	return annotationsIterator(outline, condition);
}

/**
 * @param {Object} outline
 * @param {Function} [condition] If given, filters annotations according to the given function (receives an annotation, returns a boolean).
 * @return {Generator<Object>}
 */
function annotationsIterator(outline, condition) {
	const makeIterator = function * () {
		for (const outlineTopic of outline) {
			for (const annotation of outlineTopic.annotations) {
				if (condition && !condition(annotation))
					continue;

				yield annotation;
			}
		}
	};

	return makeIterator();
}

export function mockCitation(cit) {
	Citation = cit || originalCitation;
}

// Things are usually more useful with a default export...
export default {
	getBulkCitationData:         getBulkCitationData,
	calcBulkUpdates:             calcBulkUpdates,
	calcBulkUpdatesWithPageData: calcBulkUpdatesWithPageData,
	updateAllMissingCitations:   updateAllMissingCitations,
	missingCitationsExist:       missingCitationsExist
};
